/**
 * BED ENGAGEMENT CARD
 * Zone administrative « Bulletin d'engagement » de la fiche équipage
 * (migration 00178, cahier L15-16 et L71-76).
 *
 * Ancrage : la fiche ÉQUIPAGE et pas la fiche contact. Le BED est émis par
 * `bed_access_tokens.crew_id`, le solde par `payment_cases.crew_id`, et le
 * statut CRM vit sur `crews.status` (le contact le reçoit par la synchro
 * 00024/00109). Tout ce que cette zone montre est donc crew-scopé ; la dupliquer
 * sur le contact afficherait la même donnée deux fois.
 *
 * Elle montre : le statut BED courant, les éléments manquants (avec leur date
 * d'engagement « promis avant le … »), les signatures, le certificat médical et
 * l'état du solde. Elle permet : lever un élément à la main, forcer le statut,
 * et émettre le lien du formulaire.
 *
 * Toute la vérité vient du serveur (`get_crew_bed_overview`) : ce composant ne
 * recalcule ni la liste des manquants, ni le statut cible. Chaque action
 * renvoie l'état d'après, qu'on affiche tel quel.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, ClipboardList, FileText, LinkIcon, RotateCcw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Select, toast } from '../ui';
import { BedFormLinkDialog } from './BedFormLinkDialog';
import {
  bedAdminErrorMessage,
  getCrewBedOverview,
  resolveBedMissingItem,
  setCrewBedStatus,
  unresolveBedMissingItem,
} from '../../lib/bed/admin';
import type { BedAdminMissingItem, BedCrewOverview } from '../../lib/bed/admin';
import { usePermissions } from '../../hooks/usePermissions';
import { CREW_STATUS_COLORS, CREW_STATUS_LABELS } from '../../types/crew';
import type { CrewStatus } from '../../types/crew';

// Statuts que l'admin peut forcer depuis cette zone — miroir exact de la garde
// de `bed_set_crew_status` (00178). Les annulations et reports gardent leur
// propre chemin (bloc « Informations générales »), on ne les duplique pas ici.
const OVERRIDABLE_STATUSES: CrewStatus[] = [
  'pre_registered',
  'bed_downloaded',
  'bed_incomplete',
  'bed_incomplete_payment',
  'bed_complete',
  'completed',
];

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

/** Une ligne « élément manquant » avec son action de levée. */
function MissingItemRow({
  item,
  busy,
  onResolve,
  onUnresolve,
}: {
  item: BedAdminMissingItem;
  busy: boolean;
  onResolve: (item: BedAdminMissingItem) => void;
  onUnresolve: (item: BedAdminMissingItem) => void;
}) {
  return (
    <li
      className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${
        item.resolved ? 'border-emerald-200 bg-emerald-50' : 'border-orange-200 bg-orange-50'
      }`}
    >
      <div className="min-w-0">
        <p
          className={`text-sm font-medium ${item.resolved ? 'text-emerald-800 line-through' : 'text-orange-900'}`}
        >
          {item.label}
        </p>
        <p className="mt-0.5 text-xs text-zinc-600">
          {item.role_label}
          {item.kind === 'deferred' ? (
            <>
              {' · '}
              <span className="text-zinc-500">
                promis avant le {formatDate(item.due_date ?? null)}
              </span>
            </>
          ) : (
            <>
              {' · '}
              <span className="text-zinc-500">non renseigné</span>
            </>
          )}
          {item.resolved && (
            <>
              {' · '}
              <span className="text-emerald-700">validé le {formatDate(item.resolved_at)}</span>
            </>
          )}
        </p>
        {item.resolved && item.note && (
          <p className="mt-0.5 text-xs text-emerald-700 italic">« {item.note} »</p>
        )}
      </div>

      {item.resolved ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => onUnresolve(item)}
          className="shrink-0 text-zinc-500 hover:text-zinc-900"
        >
          <RotateCcw className="mr-1.5 size-3.5" />
          Annuler
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onResolve(item)}
          className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        >
          <Check className="mr-1.5 size-3.5" />
          Valider
        </Button>
      )}
    </li>
  );
}

interface BedEngagementCardProps {
  crewId: string;
  crewName: string;
  /** Remonte à la fiche que le statut de l'équipage a bougé (badge, timeline). */
  onStatusChanged?: () => void;
}

export function BedEngagementCard({ crewId, crewName, onStatusChanged }: BedEngagementCardProps) {
  const { canEditCrewSensitiveFields } = usePermissions();
  const isStaff = canEditCrewSensitiveFields();

  const [overview, setOverview] = useState<BedCrewOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    const result = await getCrewBedOverview(crewId);
    if (result.status === 'ok') {
      setOverview(result.data);
      setForbidden(false);
    } else {
      setOverview(null);
      setForbidden(result.status === 'forbidden');
    }
    setLoading(false);
  }, [crewId]);

  useEffect(() => {
    if (!isStaff) {
      setLoading(false);
      return;
    }
    void load();
  }, [isStaff, load]);

  // Chaque action renvoie l'état d'après : on l'applique sans recharger, et on
  // prévient la fiche si le statut a bougé (badge d'en-tête + timeline).
  const applyResult = useCallback(
    (
      result: Awaited<ReturnType<typeof resolveBedMissingItem>>,
      previousStatus: string | undefined
    ) => {
      if (result.status !== 'ok') {
        toast.error(bedAdminErrorMessage(result.status));
        return;
      }
      setOverview(result.data);
      if (result.data.bed_status !== previousStatus) {
        toast.success(`Statut BED : ${result.data.bed_status_label}`);
        onStatusChanged?.();
      }
    },
    [onStatusChanged]
  );

  async function handleResolve(item: BedAdminMissingItem) {
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await resolveBedMissingItem(crewId, item.code);
    applyResult(result, previous);
    if (result.status === 'ok') toast.success(`« ${item.label} » validé manuellement`);
    setBusy(false);
  }

  async function handleUnresolve(item: BedAdminMissingItem) {
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await unresolveBedMissingItem(crewId, item.code);
    applyResult(result, previous);
    setBusy(false);
  }

  async function handleApplyStatus() {
    if (!pendingStatus) return;
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await setCrewBedStatus(crewId, pendingStatus);
    applyResult(result, previous);
    if (result.status === 'ok') setPendingStatus('');
    setBusy(false);
  }

  // Zone strictement réservée au staff : même périmètre que la garde serveur
  // (`superadmin`/`admin`), pas d'écran vide trompeur pour les autres.
  if (!isStaff) return null;

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-5 text-zinc-400" />
          <h2 className="text-xl font-semibold text-zinc-900">Bulletin d'engagement</h2>
        </div>
        <p className="mt-2 text-sm text-zinc-400">Chargement...</p>
      </Card>
    );
  }

  if (forbidden || !overview) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-5 text-zinc-400" />
          <h2 className="text-xl font-semibold text-zinc-900">Bulletin d'engagement</h2>
        </div>
        <p className="mt-2 text-sm text-zinc-400">
          {forbidden
            ? "Vous n'avez pas accès à la zone BED."
            : 'Impossible de charger le bulletin d’engagement.'}
        </p>
      </Card>
    );
  }

  const status = overview.bed_status as CrewStatus;
  const colors = CREW_STATUS_COLORS[status] ?? {
    bg: 'bg-zinc-50',
    text: 'text-zinc-700',
    border: 'border-zinc-200',
  };
  const outstanding = overview.missing.items.filter((i) => !i.resolved);
  const resolved = overview.missing.items.filter((i) => i.resolved);
  const signatures = overview.missing.signatures ?? {};
  const certificates = overview.missing.certificates ?? {};

  return (
    <Card className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-5 text-zinc-400" />
          <h2 className="text-xl font-semibold text-zinc-900">Bulletin d'engagement</h2>
        </div>
        <Badge className={`${colors.bg} ${colors.text} ${colors.border} border`}>
          {CREW_STATUS_LABELS[status] ?? overview.bed_status_label}
        </Badge>
      </div>

      {/* Cahier L41 : quand tout est réglé, le dossier est PRÊT — le passage en
          Inscrit définitif reste une action administrative explicite. */}
      {overview.ready_for_final_registration && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <p className="text-sm text-emerald-800">
            <strong>Prêt pour l'inscription définitive.</strong> Solde réglé, aucun élément
            manquant.
          </p>
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const previous = overview.bed_status;
              const result = await setCrewBedStatus(crewId, 'completed');
              applyResult(result, previous);
              setBusy(false);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Passer en Inscrit définitif
          </Button>
        </div>
      )}

      {/* Éléments manquants (cahier L15-16, L71-76) */}
      <div className="mb-5">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-900">Éléments manquants</h3>
          {outstanding.length > 0 ? (
            <Badge className="border border-orange-200 bg-orange-50 text-orange-700">
              {outstanding.length}
            </Badge>
          ) : (
            <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">
              Aucun
            </Badge>
          )}
          {overview.info_deadline && (
            <span className="text-xs text-zinc-400">
              à fournir avant le {formatDate(overview.info_deadline)}
            </span>
          )}
        </div>

        {overview.missing.source === 'none' ? (
          <p className="text-sm text-zinc-400">
            Aucun bulletin renseigné pour cet équipage à ce jour.
          </p>
        ) : outstanding.length === 0 && resolved.length === 0 ? (
          <p className="text-sm text-emerald-700">
            Bulletin complet — aucune information manquante.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {outstanding.map((item) => (
              <MissingItemRow
                key={item.code}
                item={item}
                busy={busy}
                onResolve={handleResolve}
                onUnresolve={handleUnresolve}
              />
            ))}
            {resolved.map((item) => (
              <MissingItemRow
                key={item.code}
                item={item}
                busy={busy}
                onResolve={handleResolve}
                onUnresolve={handleUnresolve}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Pièces : signatures + certificat médical (jamais bloquant, cahier L120) */}
      <div className="mb-5 grid grid-cols-2 gap-4">
        <div>
          <h3 className="mb-1.5 text-sm font-semibold text-zinc-900">Signatures</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {(['pilot', 'copilot'] as const).map((role) => {
              const signature = signatures[role];
              return (
                <li key={role} className="flex items-center gap-2">
                  {signature ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="size-4 text-orange-500" />
                  )}
                  <span className="text-zinc-700">
                    {role === 'pilot' ? 'Pilote' : 'Copilote'}
                    {signature && (
                      <span className="text-zinc-400"> — {formatDate(signature.signed_at)}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h3 className="mb-1.5 text-sm font-semibold text-zinc-900">Certificat médical</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {(['pilot', 'copilot'] as const).map((role) => {
              const certificate = certificates[role];
              return (
                <li key={role} className="flex items-center gap-2">
                  {certificate ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <span className="size-4 text-center text-xs text-zinc-300">—</span>
                  )}
                  <span className="text-zinc-700">
                    {role === 'pilot' ? 'Pilote' : 'Copilote'}
                    {certificate ? (
                      <span className="text-zinc-400"> — {certificate.name}</span>
                    ) : (
                      <span className="text-zinc-400"> — non déposé</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Solde BED — le CRM reste seul maître du montant dû */}
      <div className="mb-5">
        <h3 className="mb-1.5 text-sm font-semibold text-zinc-900">Solde</h3>
        {overview.payment.exists ? (
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-zinc-700">
              <span>
                Dû <strong>{formatCents(overview.payment.amount_cents)}</strong>
              </span>
              <span>
                Réglé <strong>{formatCents(overview.payment.paid_cents)}</strong>
              </span>
              <span>
                Reste <strong>{formatCents(overview.payment.remaining_cents)}</strong>
              </span>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              {overview.payment.settled
                ? 'Solde intégralement réglé.'
                : 'Solde non soldé — le participant peut reprendre son paiement.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-zinc-400">Aucun dossier de solde BED pour cet équipage.</p>
        )}
      </div>

      {/* Lien du formulaire + override de statut (cahier L15) */}
      <div className="flex flex-col gap-3 border-t border-zinc-200 pt-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLinkDialogOpen(true)}
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
          >
            <FileText className="mr-2 size-4" />
            {overview.link ? 'Régénérer le lien BED' : 'Générer le lien BED'}
          </Button>
          {overview.link && (
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
              <LinkIcon className="size-3.5" />
              {overview.link.expired
                ? `lien expiré le ${formatDate(overview.link.expires_at)}`
                : `lien actif jusqu'au ${formatDate(overview.link.expires_at)}`}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-500" htmlFor="bed-status-override">
              Forcer le statut BED
            </label>
            <Select
              id="bed-status-override"
              className="w-64"
              value={pendingStatus}
              placeholder="Choisir un statut…"
              disabled={busy}
              options={OVERRIDABLE_STATUSES.map((value) => ({
                value,
                label: CREW_STATUS_LABELS[value],
              }))}
              onChange={(e) => setPendingStatus(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !pendingStatus || pendingStatus === overview.bed_status}
            onClick={handleApplyStatus}
          >
            Appliquer
          </Button>
        </div>
      </div>

      <BedFormLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        crewId={crewId}
        crewName={crewName}
        onGenerated={() => {
          void load();
          onStatusChanged?.();
        }}
      />
    </Card>
  );
}
